import React from 'react';
import NextStepsPanel from './NextStepsPanel';

// Lifted verbatim out of Dashboard.js. Prop names match the variables it used to
// close over, so the markup below is unchanged from what it was inline.
const ElectionsSection = ({
  elections,
  dj,
  electionContext,
  handleApplyToElection,
  handleOpenVoterRegistration,
  nextSteps,
}) => {
  return (
    <div id="elections-section" className="bg-white rounded-xl p-6 shadow-xl">
      {/* Dynamic header based on election state */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {electionContext.currentState === 'active-hiring' && !elections.some(election => election.has_applied) ? 'Apply to DJ!' :
           electionContext.currentState === 'active-not-hiring' ? 'Current Election' :
           electionContext.currentState === 'inactive' ? 'Your Past Applications' :
           'Elections'}
        </h2>
        {electionContext.currentState === 'active-not-hiring' && !elections.some(election => election.has_applied) && (
          <span className="px-3 py-1 text-sm font-medium text-orange-800 bg-orange-100 rounded-lg">
            Not Currently Hiring
          </span>
        )}
        {electionContext.currentState === 'inactive' && (
          <span className="px-3 py-1 text-sm font-medium text-gray-800 bg-gray-100 rounded-lg">
            Historical
          </span>
        )}
      </div>
      <div className="grid gap-6">
        {elections.map((election) => (
          <div 
            key={election.rid}
            className="group relative bg-white rounded-xl border border-gray-200 hover:border-primary hover:shadow-lg transition-all duration-300 overflow-hidden"
          >
            <div className="p-6">
              {/* Mobile Layout */}
              <div className="sm:hidden">
                <div className="flex justify-end mb-3">
                  {election.has_applied && (
                    <span className={`px-3 py-1 text-sm font-medium rounded-lg ${
                      election.dj_elections?.[0]?.election_status === 'Rejected' ? 'text-red-800 bg-red-100' :
                      ['Applied', 'Hired', 'Contract Signed', 'Vetted', 'Potential To Be Hired'].includes(election.dj_elections?.[0]?.election_status) ? 'text-yellow-800 bg-yellow-100' :
                      ['Assignment Matched', 'Assignment Confirmed'].includes(election.dj_elections?.[0]?.election_status) ? 'text-green-800 bg-green-100' :
                      'text-gray-800 bg-gray-100'
                    }`}>
                    {['Vetted', 'Potential To Be Hired'].includes(election.dj_elections?.[0]?.election_status) ? 'APPLIED' : election.dj_elections?.[0]?.election_status.toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mb-4 flex-col">
                  <span className="inline-flex items-center rounded-md px-5 py-2 text-xl font-bold shadow-md bg-blue-100 text-blue-600">
                    {election.name}
                  </span>
                  <div className="flex  gap-2">
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-base font-medium text-gray-700">{election.election_date}</span>
                  </div>
                </div>

                {!election.has_applied && (
                  <div className="mt-4 -mx-6 -mb-6 p-4 border-t border-gray-100">
                    {electionContext.currentState === 'active-hiring' ? (
                      <button 
                        onClick={() => handleApplyToElection(election.rid)}
                        className="w-full inline-flex items-center justify-center px-4 py-3 border border-transparent rounded-lg text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                      >
                        Apply Now
                        <svg className="ml-2 -mr-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </button>
                    ) : electionContext.currentState === 'active-not-hiring' && !election.has_applied ? (
                      <div className="w-full">
                        <button 
                          disabled
                          className="w-full inline-flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-500 bg-gray-100 cursor-not-allowed"
                        >
                          Applications Closed
                          <svg className="ml-2 -mr-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m0 0v2m0-2h2m-2 0H9m12-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                        <p className="text-xs text-gray-500 mt-2 text-center">
                          We're not currently accepting applications for this election.
                        </p>
                      </div>
                    ) : electionContext.currentState === 'inactive' ? (
                      <div className="w-full">
                        <button 
                          disabled
                          className="w-full inline-flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-500 bg-gray-100 cursor-not-allowed"
                        >
                          Election Completed
                          <svg className="ml-2 -mr-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                        <p className="text-xs text-gray-500 mt-2 text-center">
                          This election has already taken place.
                        </p>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>

              {/* Desktop Layout */}
              <div className="hidden sm:flex justify-between items-start gap-4">
                <div className="flex flex-col gap-2">
                  <span className="inline-flex items-center rounded-md px-6 py-2 text-2xl font-bold shadow-md bg-blue-100 text-blue-600">
                    {election.name}
                  </span>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-base font-medium text-gray-700">{election.election_date}</span>
                  </div>
                </div>
                {election.has_applied ? (
                  <span className={`px-3 py-1 text-sm font-medium rounded-lg ${
                    election.dj_elections?.[0]?.election_status === 'Rejected' ? 'text-red-800 bg-red-100' :
                    ['Applied', 'Hired', 'Contract Signed', 'Vetted', 'Potential To Be Hired'].includes(election.dj_elections?.[0]?.election_status) ? 'text-yellow-800 bg-yellow-100' :
                    ['Assignment Matched', 'Assignment Confirmed'].includes(election.dj_elections?.[0]?.election_status) ? 'text-green-800 bg-green-100' :
                    'text-gray-800 bg-gray-100'
                  }`}>
                    {['Vetted', 'Potential To Be Hired'].includes(election.dj_elections?.[0]?.election_status) ? 'APPLIED' : election.dj_elections?.[0]?.election_status.toUpperCase()}
                  </span>
                ) : electionContext.currentState === 'active-hiring' ? (
                  <button 
                    onClick={() => handleApplyToElection(election.rid)}
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                  >
                    Apply Now
                    <svg className="ml-2 -mr-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </button>
                ) : electionContext.currentState === 'active-not-hiring' && !election.has_applied ? (
                  <div className="text-center">
                    <button 
                      disabled
                      className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-500 bg-gray-100 cursor-not-allowed"
                    >
                      Applications Closed
                      <svg className="ml-2 -mr-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m0 0v2m0-2h2m-2 0H9m12-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                    <p className="text-xs text-gray-500 mt-1">We're not currently accepting applications for this election.</p>
                  </div>
                ) : electionContext.currentState === 'inactive' ? (
                  <div className="text-center">
                    <button 
                      disabled
                      className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-500 bg-gray-100 cursor-not-allowed"
                    >
                      Election Completed
                      <svg className="ml-2 -mr-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                    <p className="text-xs text-gray-500 mt-1">Already completed</p>
                  </div>
                ) : null}
              </div>
              {/* DJs who already have a DJ-Election get the same closed-applications line,
                  without the Applications Closed button (that stays for DJs who have not applied). */}
              {election.has_applied && electionContext.currentState === 'active-not-hiring' && (
                <p className="text-xs text-gray-500 mt-4 text-center sm:text-left">
                  We're not currently accepting applications for this election.
                </p>
              )}
              {!election.has_applied && electionContext.currentState === 'active-hiring' && election.election_helper_text && (
                  <div className="text-sm text-gray-500 italic mb-4 mt-4">
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span>{election.election_helper_text}</span>
                    </div>
                  </div>
                )}

              {/* Show historical context for inactive elections */}
              {electionContext.currentState === 'inactive' && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 mt-0.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">Past Election</p>
                      <p className="text-xs text-gray-500">
                        This election took place on {election.election_date}. 
                        {election.has_applied ? ' You participated in this election.' : ' You did not participate in this election.'}
                      </p>
                      {election.has_applied && election.dj_elections?.[0]?.election_status && (
                        <p className="text-xs text-gray-500 mt-1">
                          Status: <span className="font-medium">{election.dj_elections[0].election_status}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {election.has_applied && (
                <div className="space-y-4">

                  <div className="mt-3 text-sm bg-blue-50 p-3 rounded-lg">


                      {['Applied', 'Vetted', 'Potential To Be Hired'].includes(election.dj_elections?.[0]?.election_status) && (
                        <p className="text-blue-800">
                          Thanks for applying! 
                        </p>
                      )}
                      Find your polling place{' '}
                      <button
                        type="button"
                        onClick={() => handleOpenVoterRegistration(election)}
                        className="text-blue-600 hover:text-blue-800 underline font-medium"
                      >
                        here
                      </button>

                  </div>



                  {/* Next Steps Section */}
                  <div className="mt-6 bg-white rounded-lg border border-gray-200 shadow-sm">
                    <div className="p-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900">Next Steps</h3>
                    </div>
                    <div className="p-4">
                      <NextStepsPanel
                        nextSteps={nextSteps}
                        election={election}
                        dj={dj}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {electionContext.currentState === 'inactive' && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 mt-0.5 flex-shrink-0 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-blue-800 mb-1">Your Application History</p>
              <p className="text-xs text-blue-700">
                These are elections you've applied to in the past. Watch for announcements about future DJ opportunities!
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ElectionsSection;
